// ./src/components/features/EventGuests/FamilyPicker.tsx
//
// Pick FAMILIES from the address book for an invitation list (ROADMAP A2b #62 P6(c) Phase B — owner's test run, 2026-09-29:
// "selecting one guest at a time is too hard… select by tags so the user can add family and/or friends… choose which adult
// of a family to send the invitation, or all adults").
//
// Search and/or tags narrow the address book; results come back GROUPED — each household once, with every member (the
// ones the search did not match too), then each person in no household. Kids are shown as children and ride along in the
// family's size; each ADULT with an email or phone can be ticked to receive their own link to the family's ONE invitation.
// "Add" hands the family to the drawer as one invitation row (`familyToPicked`).
//
// ⓘ Why not PersonPicker (the reuse gate): it picks ONE person from a dropdown; a family needs its members side by side
//   with a tick per adult. Its create-in-place drawer (`ContactEditDrawer`) is reused as is.
// ⚠ All reads are the existing contact reads (personList by keyword / tags / household, personGroupList) — no new API.

import React, { useEffect, useMemo, useState } from "react";
import { Badge, Box, Button, Checkbox, Group, Paper, Stack, Text, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import { Person, PersonGroup, PersonGroupService, PersonService, listAllPages } from "@/utils/api";
import { FamilyOption, PickedContact, buildFamilyOptions, defaultReceivers, familyToPicked } from "@/utils/inviteRows";
import { PersonGroupsProvider } from "@/contexts";
import { AppIcon, CardListSkeleton, ErrorAlert, TagsSelectorInput } from "@/components/generic";
import { ContactEditDrawer } from "@/components/features/Contacts/ContactEditDrawer/ContactEditDrawer";
import { formatPhone } from "@em/utils";
import { useCreateInPlace } from "@/hooks/useCreateInPlace";
import { CREATE_IN_OWN_ACCOUNT } from "@/utils/createInPlace";

/** How many contacts one search shows — families are expanded from these. */
const PICKER_PAGE_SIZE = 30;

export interface FamilyPickerProps {
  /** Contacts already on the event — shown as "on the list", never tickable. */
  excludePersonIds: string[];
  /** Contacts already picked in this drawer (leads and linked adults) — their families show as added. */
  pickedPersonIds: string[];
  /** A family (or a single contact) becomes one invitation row. */
  onPick: (picked: PickedContact) => void;
}

export const FamilyPicker: React.FC<FamilyPickerProps> = ({ excludePersonIds, pickedPersonIds, onPick }) => {
  const { t } = useTranslation(["events", "common"]);
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search, 300);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [options, setOptions] = useState<FamilyOption[]>([]);
  const [receivers, setReceivers] = useState<Record<string, string[]>>({});
  // ⚠ Not loaded yet is not empty — the first read decides between results, "no match" and "no contacts".
  const [isLoaded, setIsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<OpResult | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // #142 — a contact lands in the caller's own account: never offered to a crew member.
  const canCreate = useCreateInPlace(CREATE_IN_OWN_ACCOUNT);
  const [reloadKey, setReloadKey] = useState(0);
  // More contacts match than one read shows — said, so a host does not conclude the rest are missing.
  const [moreThanShown, setMoreThanShown] = useState(false);

  const onList = useMemo(() => new Set(excludePersonIds.filter(Boolean)), [excludePersonIds]);
  const picked = useMemo(() => new Set(pickedPersonIds.filter(Boolean)), [pickedPersonIds]);
  const narrowed = !!debounced.trim() || tagIds.length > 0;

  useEffect(() => {
    let alive = true;

    const load = async () => {
      setLoading(true);
      setError(null);

      const found = await PersonService.list(
        { ...(debounced.trim() ? { searchKeyword: debounced.trim() } : {}), ...(tagIds.length > 0 ? { tagIds } : {}) },
        { page: 1, pageSize: PICKER_PAGE_SIZE },
        [{ name: "lastName", order: "ASC" }, { name: "firstName", order: "ASC" }],
      );

      if (!alive) {
        return;
      }

      if (found.code !== OP_RESULT_CODES.OK) {
        setError(found);
        setOptions([]);
        setIsLoaded(true);
        setLoading(false);

        return;
      }

      const people = (Array.isArray(found.data) ? found.data : []) as Person[];
      const foundTotal = Number((found as unknown as { total?: number }).total);

      setMoreThanShown(Number.isFinite(foundTotal) ? foundTotal > people.length : people.length >= PICKER_PAGE_SIZE);
      const householdIds = Array.from(new Set(people.map((p) => p.personGroupId).filter((id): id is string => !!id)));
      let members: Person[] = [];
      const names: Record<string, string | null> = {};

      if (householdIds.length > 0) {
        const [memberRes, groupRes] = await Promise.all([
          // The households' members are read WHOLE — a cap here cut a family short (ROADMAP #118).
          listAllPages(PersonService.list, { personGroupId: householdIds }),
          PersonGroupService.list({ id: householdIds }, { page: 1, pageSize: householdIds.length }),
        ]);

        if (!alive) {
          return;
        }

        // ⚠ A family shown with members missing would be invited at the wrong size — say it failed instead (a failed
        //   page, or more members than a whole read takes, fails the read rather than cutting it).
        if (memberRes.code !== OP_RESULT_CODES.OK) {
          setError(memberRes);
          setOptions([]);
          setIsLoaded(true);
          setLoading(false);

          return;
        }

        members = (Array.isArray(memberRes.data) ? memberRes.data : []) as Person[];

        // The household's NAME is only a label — without it the family is named after the person found.
        for (const g of (groupRes.code === OP_RESULT_CODES.OK && Array.isArray(groupRes.data) ? groupRes.data : []) as PersonGroup[]) {
          if (g.id) {
            names[g.id] = g.name ?? null;
          }
        }
      }

      const next = buildFamilyOptions(people, members, names, new Date());

      setOptions(next);
      setReceivers((prev) => {
        const out = { ...prev };

        for (const f of next) {
          if (!out[f.key]) {
            out[f.key] = defaultReceivers(f, onList);
          }
        }

        return out;
      });
      setIsLoaded(true);
      setLoading(false);
    };

    void load();

    return () => {
      alive = false;
    };
    // onList is read for the first default only; a change to it must not refetch the address book.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, tagIds, reloadKey]);

  const toggle = (familyKey: string, personId: string, on: boolean) => {
    setReceivers((prev) => {
      const current = prev[familyKey] ?? [];

      return { ...prev, [familyKey]: on ? [...current.filter((id) => id !== personId), personId] : current.filter((id) => id !== personId) };
    });
  };

  const tickAll = (family: FamilyOption) => {
    setReceivers((prev) => ({
      ...prev,
      [family.key]: family.members.filter((m) => m.canReceive && !onList.has(m.personId)).map((m) => m.personId),
    }));
  };

  const add = (family: FamilyOption) => {
    // Kept in the family's own order (primary first), whatever order the ticks were made in. ⚠ Never someone who has
    // joined the list since they were ticked (the list is re-read after every import) — they have an invitation.
    const ticked = (receivers[family.key] ?? []).filter((id) => !onList.has(id));
    const pick = familyToPicked(family, family.members.map((m) => m.personId).filter((id) => ticked.includes(id)), onList);

    if (pick) {
      onPick(pick);
    }
  };

  const renderFamily = (family: FamilyOption) => {
    const isHousehold = !!family.householdId && family.members.length > 1;
    const alreadyPicked = family.members.some((m) => picked.has(m.personId));
    const ticked = (receivers[family.key] ?? []).filter((id) => !onList.has(id));
    const receivable = family.members.filter((m) => m.canReceive && !onList.has(m.personId));
    const nobodyCan = !family.members.some((m) => m.canReceive);

    return (
      <Paper key={family.key} withBorder radius="md" p="sm">
        <Stack gap="xs">
          <Group justify="space-between" wrap="nowrap" gap="sm" align="flex-start">
            <Box style={{ minWidth: 0 }}>
              <Text size="md" fw={600} lineClamp={1}>
                {family.name}
              </Text>
              {isHousehold ? (
                <Text size="md" c="dimmed">
                  {t("events:invites.family.size", { defaultValue: "{{count}} people", count: family.members.length })}
                </Text>
              ) : null}
            </Box>
            <Button
              size="md"
              variant={alreadyPicked ? "default" : "light"}
              disabled={alreadyPicked || (ticked.length === 0 && !nobodyCan)}
              leftSection={<AppIcon iconName={alreadyPicked ? "Check" : "Plus"} size={16} />}
              onClick={() => add(family)}
            >
              {alreadyPicked ? t("events:invites.family.added", { defaultValue: "Added" }) : t("common:actions.add", { defaultValue: "Add" })}
            </Button>
          </Group>

          {family.members.map((m) => {
            const contact = m.email || (m.phone ? formatPhone(m.phone) : null);
            const isOnList = onList.has(m.personId);
            const note = isOnList
              ? t("events:invites.family.invited", { defaultValue: "already invited" })
              : m.isMinor
                ? null
                : !m.canReceive
                  ? t("events:invites.family.noContact", { defaultValue: "no email or phone — comes along" })
                  : contact;

            return (
              <Group key={m.personId} gap="sm" wrap="nowrap" align="flex-start">
                {m.canReceive && !isOnList ? (
                  <Checkbox
                    size="md"
                    mt={2}
                    checked={ticked.includes(m.personId)}
                    disabled={alreadyPicked}
                    onChange={(e) => toggle(family.key, m.personId, e.currentTarget.checked)}
                    aria-label={t("events:invites.family.receives", { defaultValue: "{{name}} receives the invitation", name: m.name })}
                  />
                ) : (
                  <Box w={20} />
                )}
                <Box style={{ minWidth: 0 }}>
                  <Group gap={6} wrap="nowrap">
                    <Text size="md" lineClamp={1}>
                      {m.name}
                    </Text>
                    {m.isMinor ? (
                      <Badge size="md" variant="light" color="grape">
                        {t("events:invites.family.child", { defaultValue: "Child" })}
                      </Badge>
                    ) : null}
                  </Group>
                  {note ? (
                    <Text size="md" c="dimmed" lineClamp={1}>
                      {note}
                    </Text>
                  ) : null}
                </Box>
              </Group>
            );
          })}

          {isHousehold && receivable.length > 1 && ticked.length < receivable.length && !alreadyPicked ? (
            <Button variant="subtle" size="md" px={0} style={{ alignSelf: "flex-start" }} onClick={() => tickAll(family)}>
              {t("events:invites.family.allAdults", { defaultValue: "Send to all adults" })}
            </Button>
          ) : null}
          {nobodyCan ? (
            <Text size="md" c="orange.8">
              {t("events:invites.family.nobodyReachable", {
                defaultValue: "Nobody here has an email or phone — they go on the list, but the invitation can't be sent to them.",
              })}
            </Text>
          ) : receivable.length === 0 && !alreadyPicked ? (
            <Text size="md" c="dimmed">
              {t("events:invites.family.allInvited", { defaultValue: "Everyone here who can receive it is already invited." })}
            </Text>
          ) : ticked.length === 0 && !alreadyPicked ? (
            <Text size="md" c="dimmed">
              {t("events:invites.family.tickSomeone", { defaultValue: "Tick who receives the invitation." })}
            </Text>
          ) : null}
          {isHousehold && ticked.length > 1 ? (
            <Text size="md" c="dimmed">
              {t("events:invites.family.oneInvitation", {
                defaultValue: "One invitation for the family — each ticked adult gets their own link to it, and the family is counted once.",
              })}
            </Text>
          ) : null}
        </Stack>
      </Paper>
    );
  };

  const renderList = () => {
    if (!isLoaded) {
      return <CardListSkeleton rows={3} actions={1} actionsOn="all" actionButton={{ height: 42, width: 90 }} padding="sm" lines={3} lineGap={13} />;
    }

    if (error) {
      return (
        <ErrorAlert
          error={error}
          title={t("events:invites.family.loadFailed", { defaultValue: "Could not load your contacts" })}
          page="Invite list"
          operation="personList"
        />
      );
    }

    if (options.length === 0 && moreThanShown) {
      return (
        <Text size="md" c="dimmed" ta="center" py="md">
          {t("events:invites.family.tooMany", { defaultValue: "Too many contacts to show at once — search for a name, or pick a tag." })}
        </Text>
      );
    }

    if (options.length === 0) {
      return (
        <Text size="md" c="dimmed" ta="center" py="md">
          {narrowed
            ? t("events:invites.family.noMatch", { defaultValue: "No contacts match your search or tags." })
            : t("events:invites.family.none", { defaultValue: "No contacts yet — create one, or paste emails above." })}
        </Text>
      );
    }

    return (
      <Stack gap="xs">
        {options.map(renderFamily)}
        {moreThanShown ? (
          <Text size="md" c="dimmed" ta="center">
            {t("events:invites.family.more", { defaultValue: "More contacts match — search for a name, or pick a tag, to find the rest." })}
          </Text>
        ) : null}
      </Stack>
    );
  };

  return (
    <Stack gap="sm">
      <Group gap="sm" align="flex-end" wrap="wrap">
        <TextInput
          size="md"
          style={{ flex: 1, minWidth: 200 }}
          label={t("events:invites.family.searchLabel", { defaultValue: "Or pick from your contacts" })}
          placeholder={t("events:invites.family.search", { defaultValue: "Search names, emails, phones…" })}
          leftSection={<AppIcon iconName="Search" size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
        {canCreate ? (
          <Button variant="default" size="md" leftSection={<AppIcon iconName="UserPlus" size={16} />} onClick={() => setCreateOpen(true)}>
            {t("events:guests.picker.createLabel", { defaultValue: "Create new contact" })}
          </Button>
        ) : null}
      </Group>
      <TagsSelectorInput
        label={t("events:invites.family.tags", { defaultValue: "Tags" })}
        placeholder={t("events:invites.family.tagsPlaceholder", { defaultValue: "Any tag — e.g. Family, Friends" })}
        value={tagIds}
        onChange={(_name, ids) => setTagIds(ids)}
        allowCreate={false}
        maxTags={20}
        size="md"
      />
      <Box pos="relative" style={{ opacity: loading && isLoaded ? 0.6 : 1 }}>
        {renderList()}
      </Box>

      <PersonGroupsProvider>
        <ContactEditDrawer
          opened={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreate={(params) => PersonService.create(params)}
          onCreated={(person) => {
            setCreateOpen(false);

            // ⚠ Created INTO a household: show that family (with everyone in it) rather than picking the new contact alone —
            //   alone they would be invited at the wrong size, and their family's card would then read as already added.
            if (person.personGroupId) {
              setSearch([person.firstName, person.lastName].filter(Boolean).join(" ") || person.email || person.phone || "");
              setReloadKey((k) => k + 1);

              return;
            }

            setReloadKey((k) => k + 1);

            const single = buildFamilyOptions([person], [], {}, new Date())[0];
            const pick = single ? familyToPicked(single, single.members.map((m) => m.personId), onList) : null;

            if (pick) {
              onPick(pick);
            }
          }}
        />
      </PersonGroupsProvider>
    </Stack>
  );
};
